import type React from 'react'
import type { JSX, Ref, RefObject } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { BORDERS, COLOR, FONTS } from 'vars/index'
import { InputType } from './PINCodeInput.h'
import { PINCodeFieldSC } from './PINCodeInput.styled'

export interface IPINCodeField {
  ref?: Ref<HTMLInputElement>
  backgroundColorDefault?: COLOR
  backgroundColorHovered?: COLOR
  backgroundColorPressed?: COLOR
  borders?: BORDERS
  borderColorDefault?: COLOR
  borderColorHovered?: COLOR
  borderColorPressed?: COLOR
  borderColorInvalid?: COLOR
  invalid?: boolean
  disabled?: boolean
  colorDefault?: COLOR
  colorDisabled?: COLOR
  font?: FONTS
  indexField?: number
  maxLength?: number
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void
  autoFocus?: boolean
  autoComplete?: string
  timeoutDelay?: number
  refs?: RefObject<HTMLInputElement[]>
}

export function PINCodeField({
  ref,
  backgroundColorDefault = COLOR.TRANSPARENT,
  backgroundColorHovered = COLOR.TRANSPARENT,
  backgroundColorPressed = COLOR.TRANSPARENT,
  borders = BORDERS.BORDER_30,
  invalid,
  borderColorDefault = COLOR.TEXT_ICON_TERTIARY,
  borderColorHovered = COLOR.TEXT_ICON_TERTIARY,
  borderColorPressed = COLOR.TEXT_ICON_PRIMARY,
  borderColorInvalid = COLOR.ADDITIONAL_RED_2,
  colorDisabled,
  colorDefault = COLOR.TEXT_ICON_PRIMARY,
  font = FONTS.FontLg,
  indexField = 0,
  // maxLength,
  onKeyDown,
  onChange,
  autoFocus,
  disabled,
  onFocus,
  onPaste,
  autoComplete,
  timeoutDelay,
  refs,
}: IPINCodeField): JSX.Element {
  const updateByPasswordTimeoutId = useRef<ReturnType<
    typeof setTimeout
  > | null>(undefined)

  const [isCurrentValue, setIsCurrentValue] = useState<boolean>(false)
  const [typeInput, setTypeInput] = useState<InputType>(InputType.Tel)

  const value = refs?.current[indexField] ? refs?.current[indexField].value : ''

  useEffect(() => {
    setIsCurrentValue(Boolean(value))
  }, [value])

  const clearInput = useCallback(() => {
    clearTimeout(updateByPasswordTimeoutId.current!)
    setTypeInput(InputType.Tel)
  }, [])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(e)
      setIsCurrentValue(Boolean(e.target.value))
    },
    [onChange]
  )

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) onKeyDown(e)
      setIsCurrentValue(false)
      clearInput()
    },
    [onKeyDown, clearInput]
  )

  useEffect(() => {
    if (isCurrentValue) {
      clearTimeout(updateByPasswordTimeoutId.current!)
      updateByPasswordTimeoutId.current = setTimeout(() => {
        setTypeInput(InputType.Password)
      }, timeoutDelay)
    } else {
      clearInput()
    }
  }, [isCurrentValue, clearInput, timeoutDelay])

  useEffect(() => {
    return () => {
      clearTimeout(updateByPasswordTimeoutId.current!)
    }
  }, [])

  return (
    <PINCodeFieldSC
      ref={ref}
      type={typeInput}
      pattern='^\d*$'
      inputMode='numeric'
      min={'0'}
      max={'9'}
      onChange={handleChange}
      onKeyDown={handleOnKeyDown}
      autoFocus={autoFocus}
      backgroundColorDefault={backgroundColorDefault}
      backgroundColorHovered={backgroundColorHovered}
      backgroundColorPressed={backgroundColorPressed}
      borders={borders}
      colorDefault={colorDefault}
      colorDisabled={colorDisabled}
      invalid={invalid}
      active={isCurrentValue}
      font={font}
      disabled={disabled}
      borderColorDefault={borderColorDefault}
      borderColorHovered={borderColorHovered}
      borderColorPressed={borderColorPressed}
      borderColorInvalid={borderColorInvalid}
      data-id={indexField}
      data-testid={`test-input-${indexField}`}
      onFocus={onFocus}
      onPaste={onPaste}
      autoComplete={autoComplete}
    />
  )
}
